/**
 * TABLE NAVIGATION HOOK
 * Keyboard navigation for universal table
 * @version 1.0.0
 * @production-ready
 */

import { useState, useCallback } from 'react';
import { CellPosition, ColumnDefinition } from '../types';

export interface UseTableNavigationReturn {
  focusedCell: CellPosition;
  setFocusedCell: (position: CellPosition) => void;
  moveFocus: (direction: 'up' | 'down' | 'left' | 'right') => void;
  handleKeyDown: (
    e: React.KeyboardEvent<HTMLInputElement>,
    currentRow: number,
    currentCol: number
  ) => void;
  isCellFocused: (row: number, col: number) => boolean;
}

export const useTableNavigation = (
  totalRows: number,
  totalCols: number,
  editableColumns: ColumnDefinition[],
  onNavigate?: (row: number, col: number) => void
): UseTableNavigationReturn => {
  const [focusedCell, setFocusedCell] = useState<CellPosition>({
    rowIndex: 0,
    colIndex: 0
  });

  /**
   * Check if column is editable
   */
  const isColumnEditable = useCallback((colIndex: number): boolean => {
    const column = editableColumns[colIndex];
    return column?.editable !== false && !column?.calculated;
  }, [editableColumns]);

  /**
   * Find next editable column
   */
  const findNextEditableColumn = useCallback((
    startCol: number,
    direction: 1 | -1
  ): number => {
    let col = startCol;
    const iterations = totalCols;

    for (let i = 0; i < iterations; i++) {
      col = col + direction;
      if (col < 0) col = totalCols - 1;
      if (col >= totalCols) col = 0;

      if (isColumnEditable(col)) {
        return col;
      }
    }

    return startCol;
  }, [totalCols, isColumnEditable]);

  /**
   * Move focus in a direction
   */
  const moveFocus = useCallback((direction: 'up' | 'down' | 'left' | 'right') => {
    setFocusedCell(prev => {
      let newRow = prev.rowIndex;
      let newCol = prev.colIndex;

      switch (direction) {
        case 'up':
          newRow = Math.max(0, prev.rowIndex - 1);
          break;
        case 'down':
          newRow = Math.min(totalRows - 1, prev.rowIndex + 1);
          break;
        case 'left':
          newCol = findNextEditableColumn(prev.colIndex, -1);
          break;
        case 'right':
          newCol = findNextEditableColumn(prev.colIndex, 1);
          break;
      }

      const newPosition = { rowIndex: newRow, colIndex: newCol };
      
      if (onNavigate && (newRow !== prev.rowIndex || newCol !== prev.colIndex)) {
        setTimeout(() => onNavigate(newRow, newCol), 50);
      }

      return newPosition;
    });
  }, [totalRows, findNextEditableColumn, onNavigate]);

  /**
   * Handle keyboard events
   */
  const handleKeyDown = useCallback((
    e: React.KeyboardEvent<HTMLInputElement>,
    currentRow: number,
    currentCol: number
  ) => {
    const navigationKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', 'Tab'];
    
    if (!navigationKeys.includes(e.key)) return;

    e.preventDefault();

    let newRow = currentRow;
    let newCol = currentCol;

    switch (e.key) {
      case 'ArrowUp':
        newRow = Math.max(0, currentRow - 1);
        break;
      case 'ArrowDown':
      case 'Enter':
        newRow = Math.min(totalRows - 1, currentRow + 1);
        break;
      case 'ArrowLeft':
        if (e.shiftKey) {
          newCol = findNextEditableColumn(currentCol, -1);
        } else {
          newCol = Math.max(0, currentCol - 1);
        }
        break;
      case 'ArrowRight':
      case 'Tab':
        if (e.shiftKey) {
          newCol = Math.max(0, currentCol - 1);
        } else {
          newCol = Math.min(totalCols - 1, currentCol + 1);
        }
        break;
    }

    const newPosition = { rowIndex: newRow, colIndex: newCol };
    setFocusedCell(newPosition);

    if (onNavigate && (newRow !== currentRow || newCol !== currentCol)) {
      setTimeout(() => onNavigate(newRow, newCol), 50);
    }
  }, [totalRows, totalCols, findNextEditableColumn, onNavigate]);

  /**
   * Check if cell is focused
   */
  const isCellFocused = useCallback((row: number, col: number): boolean => {
    return focusedCell.rowIndex === row && focusedCell.colIndex === col;
  }, [focusedCell]);

  return {
    focusedCell,
    setFocusedCell,
    moveFocus,
    handleKeyDown,
    isCellFocused
  };
};
